// Building3D.tsx — Buildings Set1 §6.2: renders a non-prototype `Mod_Building` in the 3D mode.
// This is the 3D equivalent of the 2D plan overlays (`Building/PlanOverlays2D.tsx`, mounted from
// `MapObjectUI`). P3 implemented the floors + walls + openings geometry as meshes with a
// placeholder `MeshStandardMaterial`; P6 swaps in `SurfaceMaterialManager` keyed by recipe id, so
// every semantic material slot (floorTop, wallExterior, windowGlass, roofSurface, ...) gets a
// real procedural material.
//
// Pattern mirrors `TerrainRenderer`'s imperative approach: resolve the §3.7 plan via the reactive
// accessor, build pure `MeshPlan`s once per `geometryKey` (§6.4 sharing), convert to
// `THREE.BufferGeometry` (with per-group `materialIndex`), and mount one `THREE.Mesh` per
// room-instance at its building-local Z. Geometries + materials are disposed on change (the
// classic leak).
import {observer_mgl, RunInAction} from "mobx-graphlink";
import React from "react";
import * as THREE from "three";
import {useThree} from "@react-three/fiber";
import {Invalidate3JS} from "../../../../Utils/ThreeJS/Invalidate3JS.js";
import {DMap} from "../../../../Server/DB/maps/@Map.js";
import {MapObject} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {Mod_Building} from "../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_Building.js";
import {MapState} from "../../../../Store/main/maps/@MapState.js";
import {ResolveBuildingGeometryPlan, ResolveBuildingRoof} from "../../../../Server/DB/mapObjects/BuildingGeometryAccessors.js";
import {ResolveBuildingMaterials} from "../../../../Server/DB/mapObjects/BuildingAccessors.js";
import {BuildBuildingMeshPlans} from "../../../../Building/Geometry/BuildBuildingMeshPlans.js";
import type {MeshPlan} from "../../../../Building/Geometry/MeshPlan.js";
import type {ResolvedRoomMaterials} from "../../../../Server/DB/mapObjects/Building/BuildingGeometry.js";
import {SurfaceMaterialManager} from "../../../../Building/Materials/SurfaceMaterialManager.js";
import {
	BUILDING_OPAQUE_SLOT_MAP, BUILDING_TRANSPARENT_SLOT_MAP, ROOF_SLOT_MAP,
	BuildMaterialArrayCells, CollectBuildingMaterialRecipeIDs, type MaterialArrayCell,
} from "./Building/SurfaceMaterials_Shared.js";
import {buildingRenderStats} from "../../../../Building/BuildingRenderStats.js";

/** Convert a pure `MeshPlan` into a `THREE.BufferGeometry` (non-indexed, with per-group
 *  `materialIndex` so the geometry can carry a material ARRAY aligned to its groups). */
function meshPlanToGeometry(plan: MeshPlan): THREE.BufferGeometry {
	const geo = new THREE.BufferGeometry();
	geo.setAttribute("position", new THREE.Float32BufferAttribute(plan.positions, 3));
	geo.setAttribute("normal", new THREE.Float32BufferAttribute(plan.normals, 3));
	geo.setAttribute("uv", new THREE.Float32BufferAttribute(plan.uvs, 2));
	// Per-group material index: the renderer builds a material array aligned with these groups
	// (three indexes `material[group.materialIndex]`). Non-indexed geometry: three.js group
	// `start`/`count` are in VERTEX units, and `MeshPlan.groups[].start/count` are ALSO already in
	// vertex units (from `MeshBuilder`: `positions.length/3`, count = vertex count) — so pass them
	// through unchanged. (Multiplying by 3 made every group start/count 3× too large, so the LAST
	// group — e.g. the ceiling plane — pointed past the end of the position array and rendered
	// nothing / leaked garbage from adjacent groups.)
	if (plan.groups.length) {
		geo.groups = plan.groups.map((g, i)=>({start: g.start, count: g.count, materialIndex: i}));
	} else {
		geo.groups = [];
	}
	geo.computeBoundingSphere();
	geo.computeBoundingBox();
	return geo;
}

/** Resolve a per-group material-array cell (a recipe id) to a cloned `THREE.ShaderMaterial` via
 *  `SurfaceMaterialManager`, caching the cloned materials keyed by recipe id for this mount. */
async function cloneMaterialForCell(
	manager: SurfaceMaterialManager, mapID: string, cell: MaterialArrayCell,
	cache: Map<string, THREE.ShaderMaterial>,
): Promise<THREE.ShaderMaterial> {
	let clone = cache.get(cell.recipeID);
	if (clone == null) {
		const entry = await manager.getSurfaceMaterial(mapID, cell.recipeID, {transparent: cell.transparent});
		clone = manager.cloneSurfaceMaterial(entry);
		cache.set(cell.recipeID, clone);
	}
	return clone;
}

export const Building3D = observer_mgl((_props: {obj: MapObject, map: DMap, mapState: MapState, mod: Mod_Building})=>{
	const {obj, map} = _props;
	// `invalidate` is required because the 3D Canvas runs with `frameloop="demand"` — R3F only
	// re-renders when it's told to. This component mounts meshes IMPERATIVELY (scene.add/group.add
	// in an effect), which does NOT auto-invalidate; without a call after mounting, a live edit
	// that re-mounts the room meshes would never be drawn (only the async-mounted roof, which
	// lands in a later invalidated frame, shows). Calling `invalidate()` after each mount makes
	// demand-mode render the newly-added meshes.
	const {scene, camera, gl, invalidate} = useThree();

	const plan = ResolveBuildingGeometryPlan(obj);
	const roof = ResolveBuildingRoof(obj);
	const roofMaterials = ResolveBuildingMaterials(obj) as unknown as ResolvedRoomMaterials;
	const meshPlans = React.useMemo(()=>BuildBuildingMeshPlans(plan), [plan, obj.id]);

	// P6 — one SurfaceMaterialManager per mount, alive for this component's lifetime.
	const managerRef = React.useRef<SurfaceMaterialManager|null>(null);
	if (managerRef.current == null) managerRef.current = new SurfaceMaterialManager();
	const manager = managerRef.current;

	// P7 — program pre-warm: attach the three renderer (mirrors `TerrainRenderer`'s Phase-6
	// `manager.attachRenderer(gl)`), and schedule a compile-ahead of the building's DISTINCT
	// recipe set OFF the frame loop (a macrotask), so the first live appearance of each building
	// material is a three.js program-cache HIT (no first-draw shader-compile hitch when walking
	// up to a building). Best-effort + idempotent; re-schedules when the material set changes.
	React.useEffect(()=>{
		manager.attachRenderer(gl);
		const ids = CollectBuildingMaterialRecipeIDs(roofMaterials);
		if (ids.length == 0) return;
		const timer = setTimeout(()=>{
			manager.warmUpPrograms(ids).catch((err: unknown)=>console.warn("[Building3D] material pre-warm failed (best-effort; live compile still works).", err));
		}, 0);
		return ()=>clearTimeout(timer);
	}, [manager, gl, roofMaterials]);

	// Build opaque + transparent BufferGeometries per geometry key (with per-group materialIndex).
	const [opaqueGeos, transparentGeos] = React.useMemo(()=>{
		const o = new Map<string, THREE.BufferGeometry>();
		const t = new Map<string, THREE.BufferGeometry>();
		if (meshPlans != null) {
			for (const [key, set] of meshPlans) {
				if (set.opaque.positions.length) o.set(key, meshPlanToGeometry(set.opaque));
				if (set.transparent.positions.length) t.set(key, meshPlanToGeometry(set.transparent));
			}
		}
		return [o, t];
	}, [meshPlans]);

	// P5: build the roof BufferGeometry from the resolved roof MeshPlan (building-local frame).
	const roofGeo = React.useMemo(()=>{
		if (roof == null || roof.mesh.positions.length == 0) return null;
		return meshPlanToGeometry(roof.mesh);
	}, [roof]);

	// Per-mount material clones cached by recipe id (shared across geometry keys).
	const materialCacheRef = React.useRef<Map<string, THREE.ShaderMaterial>>(new Map());
	const materialCache = materialCacheRef.current;

	// Per-FLOOR-geometryKey material arrays (opaque/transparent). Built async on mount + when the
	// plan's material sets change, held in STATE (not a ref) so a completed build is a NEW map
	// reference that reliably re-triggers the mount effect below. A ref+boolean version failed:
	// React 18 batches the reset-to-false and the async set-true, so the boolean never visibly
	// changed and the mount effect never re-ran with the freshly-built (new-geometry-key) map —
	// the rooms vanished after an edit and only a remount brought them back.
	//
	// Set2 §4.2/§7: the key is now per-FLOOR. The materials for a floor are the FIRST room's on that
	// floor (a floor's slots come from its rooms; §11 records per-wall material overrides as the
	// future refinement for a multi-room floor whose rooms disagree).
	const [roomMats, setRoomMats] = React.useState<Map<string, {opaque: THREE.ShaderMaterial[]; transparent: THREE.ShaderMaterial[]}>>(new Map());

	React.useEffect(()=>{
		let cancelled = false;
		(async ()=>{
			const out = new Map<string, {opaque: THREE.ShaderMaterial[]; transparent: THREE.ShaderMaterial[]}>();
			if (meshPlans != null) {
				for (const [key, set] of meshPlans) {
					let floorMaterials: ResolvedRoomMaterials|null = null;
					for (const floor of plan?.floors ?? []) {
						if (floor.geometryKey == key) { floorMaterials = floor.rooms[0]?.materials ?? null; break; }
					}
					if (floorMaterials == null) continue;
					const opaque = BuildMaterialArrayCells(set.opaque.groups, floorMaterials, BUILDING_OPAQUE_SLOT_MAP, BUILDING_TRANSPARENT_SLOT_MAP);
					const transparent = BuildMaterialArrayCells(set.transparent.groups, floorMaterials, BUILDING_OPAQUE_SLOT_MAP, BUILDING_TRANSPARENT_SLOT_MAP);
					const opaqueMats: THREE.ShaderMaterial[] = [];
					for (const cell of opaque) opaqueMats.push(await cloneMaterialForCell(manager, map.id, cell, materialCache));
					const transparentMats: THREE.ShaderMaterial[] = [];
					for (const cell of transparent) transparentMats.push(await cloneMaterialForCell(manager, map.id, cell, materialCache));
					out.set(key, {opaque: opaqueMats, transparent: transparentMats});
				}
			}
			if (!cancelled) setRoomMats(out);
		})().catch((err: unknown)=>{
			console.warn("[Building3D] material build failed; rooms not mounted:", err);
		});
		return ()=>{ cancelled = true; };
	}, [meshPlans, plan, map.id, manager, materialCache]);

	// Mount the meshes imperatively; dispose geometries + materials on change/unmount.
	React.useEffect(()=>{
		if (plan == null || roomMats.size == 0) return;
		const group = new THREE.Group();
		// Building-local → world: the plan's floors/rooms/roof are in the BUILDING's LOCAL frame
		// (rotated by the building's own + ancestor rotations). Undo that accumulated Z rotation
		// (matching `MapObjectUI`'s `-objRot_world`), or a rotated building renders mis-oriented.
		group.position.set(plan.worldOrigin[0], plan.worldOrigin[1], plan.worldOrigin[2]);
		group.rotation.z = -plan.worldRotationDeg * (Math.PI / 180);
		scene.add(group);

		const meshes: THREE.Mesh[] = [];
		const cameraPos: [number, number, number] = [camera.position.x, camera.position.y, camera.position.z];

		// §8 diagnostics — live per-building mesh counts, written to `buildingRenderStats` for the
		// Stats panel. `meshCount` counts only the opaque/glass meshes actually MOUNTED (the floor
		// loops below); the roof mesh is counted via `roofCount` (added async, so we re-write).
		// `geometryKeyCount` = distinct shared BufferGeometries; floorCount/roomCount reflect the
		// resolved plan regardless of mesh mount success (so "0 meshes, N rooms" isolates a
		// geometry/material production failure from a plan-resolution failure).
		let meshCount = 0;
		let roofCount = 0;
		const writeStats = ()=>{
			RunInAction("Building3D.renderStats", ()=>{
				buildingRenderStats.buildings.set(obj.id, {
					meshCount, roofCount,
					floorCount: plan.floors.length,
					roomCount: plan.floors.reduce((acc, f)=>acc + f.rooms.length, 0),
					geometryKeyCount: opaqueGeos.size + transparentGeos.size,
				});
			});
		};

		// Set2 §4.2/§7: mounting is now per FLOOR — one mesh per unique floor geometry key, stamped
		// at each floor sharing that key. The geometry already contains the merged walls + opening
		// models + every room's slab/finish/ceiling for that floor.
		for (const floor of plan.floors) {
			if (floor.walls == null) continue; // gap floor
			const opaqueGeo = opaqueGeos.get(floor.geometryKey);
			const mats = roomMats.get(floor.geometryKey);
			if (opaqueGeo != null && mats != null && mats.opaque.length) {
				const mesh = new THREE.Mesh(opaqueGeo, mats.opaque);
				// Floor plan z is relative to the floor's floorTopZ; the plan builder emits z=0 as
				// the floor plane, so the floor's absolute (building-local) z = floor.floorTopZ.
				mesh.position.z = floor.floorTopZ;
				mesh.castShadow = true;
				mesh.receiveShadow = true;
				for (const m of mats.opaque) {
					manager.setFrameUniforms(m, {surfaceElevation: plan.worldOrigin[2] + floor.floorTopZ, cameraPos});
				}
				group.add(mesh);
				meshes.push(mesh);
				meshCount++;
			}
			const glassGeo = transparentGeos.get(floor.geometryKey);
			if (glassGeo != null && mats != null && mats.transparent.length) {
				const gm = new THREE.Mesh(glassGeo, mats.transparent);
				gm.position.z = floor.floorTopZ;
				gm.castShadow = false;
				gm.receiveShadow = false;
				for (const m of mats.transparent) {
					manager.setFrameUniforms(m, {surfaceElevation: plan.worldOrigin[2] + floor.floorTopZ, cameraPos});
				}
				group.add(gm);
				meshes.push(gm);
				meshCount++;
			}
		}

		// P5: mount the roof mesh (already in building-local coordinates, so z is absolute).
		if (roofGeo != null) {
			const roofCells = BuildMaterialArrayCells(roof!.mesh.groups, roofMaterials, ROOF_SLOT_MAP, {});
			(async ()=>{
				const roofMats: THREE.ShaderMaterial[] = [];
				for (const cell of roofCells) roofMats.push(await cloneMaterialForCell(manager, map.id, {...cell, transparent: false}, materialCache));
				if (roofMats.length) {
					const rm = new THREE.Mesh(roofGeo, roofMats);
					rm.castShadow = true;
					rm.receiveShadow = true;
					// The roof mesh is already in building-local coordinates (its z = the topmost
					// claimed floor's wallTopZ); the surface elevation = the building's world Z
					// origin (the geoHeight primitive reads the mesh's world Z).
					for (const m of roofMats) manager.setFrameUniforms(m, {surfaceElevation: plan.worldOrigin[2], cameraPos});
					group.add(rm);
					meshes.push(rm);
					roofCount++;
					writeStats();
				}
			})();
		}
		writeStats();
		// Demand-mode re-render: the meshes were added imperatively, so R3F won't draw them until
		// `invalidate()` requests a frame.
		Invalidate3JS(invalidate, "building.mount", "building meshes mounted imperatively");
		return ()=>{
			scene.remove(group);
			// §8 — remove this building's entry so the Stats panel totals stay accurate on unmount.
			RunInAction("Building3D.renderStats.clear", ()=>{
				buildingRenderStats.buildings.delete(obj.id);
			});
			for (const m of meshes) { m.geometry = new THREE.BufferGeometry(); m.material = []; } // detach
		};
}, [plan, opaqueGeos, transparentGeos, roofGeo, scene, camera, invalidate, roomMats, manager, map.id, roofMaterials, roof]);

	// Dispose geometries when the plan set changes (avoid leaking BufferGeometries).
	React.useEffect(()=>{
		return ()=>{
			for (const geo of opaqueGeos.values()) geo.dispose();
			for (const geo of transparentGeos.values()) geo.dispose();
			if (roofGeo != null) roofGeo.dispose();
		};
	}, [opaqueGeos, transparentGeos, roofGeo]);

	// Dispose the material clones + manager on unmount.
	React.useEffect(()=>{
		return ()=>{
			for (const m of materialCache.values()) m.dispose();
			manager.dispose();
		};
	}, [manager, materialCache]);

	return null;
});

